// ============================================
// STATE MANAGEMENT
// ============================================

class SaveLPState {
    constructor() {
        this.stickCount = this.initStickCount();
        this.selectedItems = [];
        this.cartPayload = [];
        this.listeners = new Map();
    }

    initStickCount() {
        const urlParams = new URLSearchParams(window.location.search);
        const urlSticks = urlParams.get('sticks');
        const storedSticks = sessionStorage.getItem('quant');

        const count = urlSticks || storedSticks || 4;
        const numCount = parseInt(count, 10);

        sessionStorage.setItem('quant', numCount);
        return numCount;
    }

    getStickCount() {
        return this.stickCount;
    }

    getMaxStickLimit() {
        const el = document.getElementById('save-lp-page-container');
        const raw = el?.getAttribute('data-max-sticks');
        if (raw != null && raw !== '') {
            const parsed = parseInt(raw, 10);
            if (!Number.isNaN(parsed)) return parsed;
        }
        return Infinity;
    }

    addSelectedItem(id) {
        if (this.selectedItems.length >= this.getMaxStickLimit()) return;
        this.selectedItems.push(id);
        this.notify('selectionChanged', this.selectedItems);
        if (this.selectedItems.length === this.getMaxStickLimit()) {
            this.notify('reachedMaxSticks', this.selectedItems);
        }
    }

    removeSelectedItem(id) {
        const index = this.selectedItems.findIndex(item => item === id);
        if (index !== -1) {
            this.selectedItems.splice(index, 1);
            this.notify('selectionChanged', this.selectedItems);
        }
    }

    clearSelectedItems() {
        this.selectedItems = [];
        this.notify('selectionChanged', this.selectedItems);
    }

    getSelectedItems() {
        return this.selectedItems;
    }

    setCartPayload(payload) {
        this.cartPayload = payload;
    }

    getCartPayload() {
        return this.cartPayload;
    }

    subscribe(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }

    notify(event, data) {
        const callbacks = this.listeners.get(event) || [];
        callbacks.forEach(cb => cb(data));
    }
}

// ============================================
// CONFIGURATION
// ============================================

class SaveLPConfig {
    constructor() {
        const data = window.saveLPData || {};

        this.pageType = data.page_type;
        this.language = data.language;
        this.discountType = data.discount_type;
        this.bundleProduct = data.bundle_product;
        this.product = data.product;
        this.bundleCollectionId = data.bundle_collection_id;
        this.discountCode = data.discount_code
        this.discountCodes = data.discount_codes
        this.sellingPlanIdFourWeek = data.selling_plan_id_four_week

        this.MAX_GIFTS = 4;
        this.SESSION_STORAGE_KEY = 'quant';
        this.URL_PARAM_STICKS = 'sticks';
        this.CHECKOUT_BTN_ID = 'save-lp-checkout-btn';
    }

    validate() {
        if (!window.saveLPData) {
            console.warn('Error: window.saveLPData is not defined.');
            return false;
        }

        const checks = [
            { value: this.pageType, name: 'page_type', type: 'string' },
            { value: this.language, name: 'language', type: 'string' },
            { value: this.discountType, name: 'discount_type', type: 'string' },
            { value: this.sellingPlanIdFourWeek, name: 'selling_plan_id_four_week', type: 'number' },
            { value: this.bundleProduct?.id, name: 'bundle_product', type: 'object' },
            { value: this.product?.id, name: 'product', type: 'object' },
            { value: this.bundleCollectionId, name: 'bundle_collection_id', type: 'number_or_string' },
            { value: this.discountCode, name: 'discount_code', type: 'string' },
        ];


        for (const check of checks) {
            if (check.type === 'string' && (typeof check.value !== 'string' || check.value.trim() === '')) {
                console.warn(`Error: ${check.name} is missing or invalid.`);
                return false;
            }
            if (check.type === 'object' && !check.value) {
                console.warn(`Error: ${check.name} is missing or invalid.`);
                return false;
            }
            if (check.type === 'number_or_string' && typeof check.value !== 'string' && typeof check.value !== 'number') {
                console.warn(`Error: ${check.name} is missing or invalid.`);
                return false;
            }
            if (check.type === 'number' && !Number.isFinite(check.value)) {
                console.warn(`Error: ${check.name} is missing or invalid.`);
                return false;
            }
        }

        return true;
    }

    getDiscountCode() {
        return this.discountCodes;
    }
}

// ============================================
// CART SERVICE
// ============================================

class CartService {
    constructor(config, state, trackingService) {
        this.config = config;
        this.state = state;
        this.trackingService = trackingService;
    }

    setSellingPlanId(count) {
        if (!this.config.sellingPlanIdFourWeek) {
            console.warn('Selling plan ID is not set');
            return null;
        }

        const selectedPlanId = this.config.sellingPlanIdFourWeek;

        return Number(selectedPlanId);
    }

    collectCartItems() {
        const selectedItems = this.state.getSelectedItems();
        const sellingPlanId = this.setSellingPlanId(selectedItems.length);

        // Deduplicate selected items into { variantId: quantity } map
        const productCounts = selectedItems.reduce((acc, id) => {
            acc[id] = (acc[id] || 0) + 1;
            return acc;
        }, {});

        const products = Object.entries(productCounts).map(([id, quantity]) => ({
            id: parseInt(id, 10),
            quantity,
            selling_plan: sellingPlanId
        }));

        return [...products].filter(item => item.quantity > 0);
    }

    collectGiftItems(container) {
        const giftElements = container.querySelectorAll('[data-gift].is-active');
        const activeGifts = Array.from(giftElements)
            .map(el => {
                const giftId = parseInt(el.getAttribute('data-gift-id'), 10);
                return Number.isInteger(giftId) && giftId > 0 ? { id: giftId, quantity: 1 } : null;
            })
            .filter(Boolean).filter(item => item.quantity > 0);

        const giftCartItems = activeGifts.map(gift => ({
            id: gift.id,
            quantity: gift.quantity
        }));

        return [...giftCartItems];
    }

    async processRechargeBundle(bundleItems) {
        if (bundleItems.length === 0) {
            console.warn('[processRechargeBundle] No items to bundle');
            return [];
        }

        const failOpen = (error, errorSource = 'recharge_bundle') => {
            console.error('Error processing recharge bundle:', error);
            if (this.trackingService) {
                this.trackingService.sendGtagCreateRechargeBundleError(error, errorSource);
            }
            return bundleItems;
        };

        if (typeof recharge === 'undefined' || !recharge.bundle) {
            return failOpen(new Error('Recharge bundle SDK not loaded'));
        }

        const bundleProduct = this.config.bundleProduct;
        const bundleConfig = {
            variantId: bundleProduct?.variants?.[0]?.id,
            productId: bundleProduct?.id,
            handle: bundleProduct?.handle,
        };

        if (!bundleConfig.productId || !bundleConfig.variantId || !bundleConfig.handle) {
            return failOpen(new Error('Bundle product configuration error: Missing required data'));
        }

        const selections = bundleItems.map(item => ({
            collectionId: this.config.bundleCollectionId,
            externalProductId: this.config.product.id,
            externalVariantId: item.id,
            quantity: item.quantity,
            sellingPlan: item.selling_plan
        }));

        const bundle = {
            externalVariantId: bundleConfig.variantId,
            externalProductId: bundleConfig.productId,
            selections: selections,
        };

        try {
            const bundledCartItems = await recharge.bundle.getDynamicBundleItems(bundle, bundleConfig.handle);
            if (this.trackingService) {
                this.trackingService.sendGtagCreateRechargeBundleEvent();
            }
            return [...bundledCartItems];
        } catch (error) {
            return failOpen(error);
        }
    }

    async prepareCheckoutPayload(container) {
        const cartItems = this.collectCartItems();
        if (!cartItems || cartItems.length === 0) {
            this.state.setCartPayload([]);
            return;
        }

        let giftItems = [];
        try {
            giftItems = this.collectGiftItems(container);
        } catch (error) {
            console.error('Error collecting gift items:', error);
        }

        try {
            const processedItems = await this.processRechargeBundle(cartItems);
            this.state.setCartPayload([...processedItems, ...giftItems]);
        } catch (error) {
            console.error('Error preparing cart payload:', error);
            if (this.trackingService) {
                this.trackingService.sendGtagCreateRechargeBundleError(error, 'prepare_checkout_payload');
            }
            this.state.setCartPayload([...cartItems, ...giftItems]);
        }
    }

    triggerConvertCheckoutEvents() {
        window._conv_q = window._conv_q || [];

        // S56/67 - Checkout
        window._conv_q.push(['triggerConversion', '100320288']);

        // Send discount-type-specific events
        if (this.config.discountType === '56') {
            // S56 Checkout (only 56)
            window._conv_q.push(['triggerConversion', '100332713']);
        } else if (this.config.discountType === '67') {
            // S67 Checkout (only 67)
            window._conv_q.push(['triggerConversion', '100332714']);
        }

        // send convert atc events based on selected stick count
        const selectedItems = this.state.getSelectedItems();
        const selectedStickCount = selectedItems.length;
        switch (selectedStickCount) {
            case 1:
                window._conv_q.push(['triggerConversion', '100333863']);
                break;
            case 2:
                window._conv_q.push(['triggerConversion', '100333864']);
                break;
            case 3:
                window._conv_q.push(['triggerConversion', '100333865']);
                break;
            case 4:
                window._conv_q.push(['triggerConversion', '100333866']);
                break;
            default:
                break;
        }

    }

    getAbTestCartAttributes() {
        const ACTIVE_TESTS = [
            {
                id: '_ab_sub_terms',
                control: 'control',
                variant: 'variant',
            },
        ];

        // Set variant based on random number
        const setVariant = (test) => Math.random() < 0.5 ? test.control : test.variant;

        // Return object with test IDs and variants
        return ACTIVE_TESTS.reduce((acc, test) => {
            acc[test.id] = setVariant(test);
            return acc;
        }, {});
    }

    checkout() {
        const payload = this.state.getCartPayload();
        if (!payload || payload.length === 0) {
            console.warn('Cart payload is empty or not ready');
            return;
        }

        if (typeof addToCart !== 'function') {
            console.warn('addToCart function is not defined. Add addToCart.js before this script.');
            return;
        }

        const discountCode = this.config.getDiscountCode();

        let redirectUrl = `/checkout?discount=${discountCode}`;
        if (this.config.language === 'es') {
            redirectUrl = `/checkout?discount=${discountCode}&locale=es`;
        }
        addToCart({
            items: payload,
            clearCart: true,
            getRedirectUrl: () => redirectUrl,
            cartAttributes: {
                offerType: 'save67',
                offerLanguage: this.config.language,
                discountType: this.config.discountType,
                pageType: `save67-${this.config.pageType}`,
                ...this.getAbTestCartAttributes()
            },
            onSuccess: (data) => {
                if (this.trackingService) {
                    this.trackingService.sendGtagSave56();
                    this.trackingService.sendGtagGoToCheckoutEvent();
                } else {
                    console.warn('Tracking service is not defined');
                }

                this.triggerConvertCheckoutEvents();
            },
            onError: (error) => {
                console.error('[CartService] Error:', error);
                if (this.trackingService) {
                    this.trackingService.sendGtagGoToCheckoutEventError(error);
                } else {
                    console.warn('Tracking service is not defined');
                }
            }
        });
    }
}

// ============================================
// TRACKING SERVICE
// ============================================

class TrackingService {
    /** Must match STORAGE_KEY / STORAGE_VALUE in assets/save-lp-ten-reasons-meta-shift.js (landing only). */
    static TEN_REASONS_META_STORAGE_KEY = 'carpe_ten_reasons_meta_shift_v1';
    static TEN_REASONS_META_STORAGE_VALUE = '1';

    static isTenReasonsMetaShiftCohort() {
        try {
            return (
                sessionStorage.getItem(TrackingService.TEN_REASONS_META_STORAGE_KEY) ===
                TrackingService.TEN_REASONS_META_STORAGE_VALUE
            );
        } catch (e) {
            return false;
        }
    }

    constructor(state, config) {
        this.state = state;
        this.config = config;
    }

    getTotalSticks() {
        const items = this.state?.getSelectedItems?.();
        return Array.isArray(items) ? items.length : -1;
    }

    formatErrorMessage(error) {
        const message = error?.message != null ? String(error.message) : String(error);
        return message.slice(0, 200);
    }

    getTrackingContext(extra = {}) {
        return {
            total_sticks: this.getTotalSticks(),
            page_type: this.config?.pageType,
            discount_type: this.config?.discountType,
            ...extra,
        };
    }

    safeTrack(checkFn, executeFn, apiName, retries = 50) {
        const api = checkFn();

        if (typeof api === 'function') {
            executeFn(api);
        } else if (retries > 0) {
            setTimeout(() => {
                this.safeTrack(checkFn, executeFn, apiName, retries - 1);
            }, 100);
        } else {
            console.warn(`${apiName} not available after timeout`);
        }
    }

    // TODO: deprecate when new alerts are live
    sendGtagSave56(retries = 50) {
        this.safeTrack(
            () => window.gtag,
            (gtag) => {
                const stickCount = this.state.getStickCount();
                gtag('event', 'save56', { save56_total_sticks: stickCount });
            },
            'gtag',
            retries
        );
    }

    sendFbAddToCart(retries = 50) {
        this.safeTrack(
            () => window.fbq,
            (fbq) => fbq('track', 'AddToCart'),
            'fbq',
            retries
        );
    }

    sendGtagViewScentSelectionEvent(retries = 50) {
        this.safeTrack(
            () => window.gtag,
            (gtag) => {
                gtag('event', 'save56_view_scent_selector');
            },
            'gtag',
            retries
        );
    }

    sendGtagGoToCheckoutEvent(retries = 50) {
        this.safeTrack(
            () => window.gtag,
            (gtag) => {
                gtag('event', 'save56_go_to_checkout', this.getTrackingContext());
            },
            'gtag',
            retries
        );
    }

    sendGtagCreateRechargeBundleEvent(retries = 50) {
        this.safeTrack(
            () => window.gtag,
            (gtag) => {
                gtag('event', 'save56_create_recharge_bundle', this.getTrackingContext());
            },
            'gtag',
            retries
        );
    }

    sendGtagCreateRechargeBundleError(error, errorSource = 'recharge_bundle', retries = 50) {
        this.safeTrack(
            () => window.gtag,
            (gtag) => {
                gtag('event', 'save56_create_recharge_bundle_error', this.getTrackingContext({
                    error_message: this.formatErrorMessage(error),
                    error_source: errorSource,
                }));
            },
            'gtag',
            retries
        );
    }

    sendGtagGoToCheckoutEventError(error, retries = 50) {
        this.safeTrack(
            () => window.gtag,
            (gtag) => {
                gtag('event', 'save56_go_to_checkout_error', this.getTrackingContext({
                    error_message: this.formatErrorMessage(error),
                    error_source: 'checkout_add',
                }));
            },
            'gtag',
            retries
        );
    }
    sendConvertGoalMaxSticks() {
        window._conv_q = window._conv_q || [];
        window._conv_q.push(["triggerConversion", "100333337"]);
    }

    sendInitialEvents() {
        this.sendGtagSave56();
        if (!TrackingService.isTenReasonsMetaShiftCohort()) {
            this.sendFbAddToCart();
        }
        this.sendGtagViewScentSelectionEvent();
    }
}

// ============================================
// UI CONTROLLERS
// ============================================

class UIInitializer {
    constructor(container, config, state) {
        this.container = container;
        this.config = config;
        this.state = state;
    }

    initAll(stickCount) {
        this.initStickCountElements(stickCount);
        this.initFreeGiftCards(stickCount);
        this.initCheckoutButtonLabel(stickCount);
        this.updateScentStickGiftUI();
        this.updateGiftCountFromActiveCards();

        if (this.config.pageType !== 'fem-care') {
            this.initProductGroupVisibility(stickCount);
        }
    }

    bindGiftCountToState() {
        if (!this.state || typeof this.state.subscribe !== 'function') return;
        this.state.subscribe('selectionChanged', () => this.updateGiftCountFromActiveCards());
    }

    // This keeps the numeric gift counter (e.g. "2/4 Free Gifts") in sync
    // with however many gift cards are currently unlocked (is-active).
    updateGiftCountFromActiveCards() {
        const activeCount = this.container.querySelectorAll('[data-gift-card].is-active').length;
        const elements = this.container.querySelectorAll('[data-gift-count-numeric]');
        elements.forEach(el => {
            el.textContent = activeCount;
        });
    }

    initStickCountElements(stickCount) {
        const elements = this.container.querySelectorAll('[data-stick-total]');

        elements.forEach(el => {
            el.textContent = stickCount;

            // Handle pluralization
            if (stickCount === 1) {
                const parent = el.parentElement;
                if (parent && parent.textContent.includes('sticks')) {
                    parent.textContent = parent.textContent.replace(/\bsticks\b/i, 'stick');
                }
            }
        });
    }

    setScentStickVariant() {
        const SCENT_STICK_VARIANT_DATA = {
            'CV': {
                id: 41302383427717,
                img: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Coconut_Vanilla_Scent_Stick_Transparent_250px_1.png?v=1754400941',
                weight: 28.25
            },
            'CL': {
                id: 41302383460485,
                img: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Clean_Sport_Scent_Stick_Transparent_250px.png?v=1754335278',
                weight: 30.25
            },
            'SP': {
                id: 41302383493253,
                img: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Cucumber_Spa_Scent_Stick_Transparent_250px_1.png?v=1754400939',
                weight: 41.5
            }
        };

        const totalWeight = Object.values(SCENT_STICK_VARIANT_DATA).reduce((sum, variant) => sum + variant.weight, 0);
        const random = Math.random() * totalWeight;

        // Select variant based on weighted random
        let cumulative = 0;
        let selectedVariant = null;

        for (const [key, variant] of Object.entries(SCENT_STICK_VARIANT_DATA)) {
            cumulative += variant.weight;
            if (random <= cumulative) {
                selectedVariant = variant;
                break;
            }
        }

        // Fallback to highest weight
        if (!selectedVariant) {
            selectedVariant = Object.values(SCENT_STICK_VARIANT_DATA)
                .sort((a, b) => b.weight - a.weight)[0];
        }

        return selectedVariant;
    }

    updateScentStickGiftUI() {
        const giftElement = this.container.querySelector('[data-gift="2"]');
        if (!giftElement) return;

        if (giftElement.getAttribute('data-gift-type') !== 'scent_stick') return;
        const selectedVariant = this.setScentStickVariant();
        giftElement.setAttribute('data-gift-id', selectedVariant.id);
        const imgElement = giftElement.querySelector('img');
        if (imgElement) {
            imgElement.src = selectedVariant.img;
        }
    }

    initFreeGiftCards(stickCount) {
        const giftCards = this.container.querySelectorAll('[data-gift-card]');
        if (!giftCards || giftCards.length === 0) {
            console.log('No gift cards found');
            return;
        }

        const clampedCount = Math.min(stickCount, this.config.MAX_GIFTS);

        giftCards.forEach((card, i) => {
            if (i < clampedCount) {
                card.classList.add('is-active');
            } else {
                card.classList.remove('is-active');
            }
        });
    }

    initCheckoutButtonLabel(stickCount) {
        const checkoutBtnText = this.container.querySelector('[data-checkout-btn-text]');
        if (!checkoutBtnText) {
            console.log('No checkout button text element found');
            return;
        }

        let checkoutBtnTextContent = `Select at least ${stickCount} stick${stickCount > 1 ? 's' : ''}`
        if (this.config.language === 'es') {
            checkoutBtnTextContent = `Selecciona al menos ${stickCount} barra${stickCount > 1 ? 's' : ''}`;
        }
        checkoutBtnText.innerHTML = checkoutBtnTextContent;
    }

    initProductGroupVisibility(stickCount) {
        if (stickCount === 1) {
            console.log('Stick count is 1, hiding bundle selector');
            return;
        }

        const productGroupContainer = this.container.querySelector('[data-product-group-container]');
        if (!productGroupContainer) {
            console.log('Product group container not found');
            return;
        }

        productGroupContainer.style.display = 'flex';
    }
}

class ProductSelectorController {
    constructor(container, state, cartService) {
        this.container = container;
        this.state = state;
        this.cartService = cartService;
        this.itemQuantDivs = Array.from(this.container.querySelectorAll('[data-item-quantity-count]'));

        this.state.subscribe('selectionChanged', () => this.updateUI());
        this.init();
    }

    init() {
        this.container.querySelectorAll('[data-action-atc-add-quick]').forEach(btn => {
            btn.addEventListener('click', () => this.handleQuickAdd(btn));
        });

        this.container.querySelectorAll('[data-action-atc-add]').forEach(btn => {
            btn.addEventListener('click', () => this.handleAdd(btn));
        });

        this.container.querySelectorAll('[data-action-atc-rm]').forEach(btn => {
            btn.addEventListener('click', () => this.handleRemove(btn));
        });

        this.updateUI();
    }

    // Extract event attachment logic into a public method that convert can use
    attachEvents(container) {
        container.querySelectorAll('[data-action-atc-add-quick]').forEach(btn => {
            btn.addEventListener('click', () => this.handleQuickAdd(btn));
        });

        container.querySelectorAll('[data-action-atc-add]').forEach(btn => {
            btn.addEventListener('click', () => this.handleAdd(btn));
        });

        container.querySelectorAll('[data-action-atc-rm]').forEach(btn => {
            btn.addEventListener('click', () => this.handleRemove(btn));
        });
    }

    handleQuickAdd(btn) {
        const limit = this.state.getMaxStickLimit();
        const current = this.state.getSelectedItems().length;
        if (current >= limit) return;

        const id = parseInt(btn.getAttribute('value'));
        this.state.addSelectedItem(id);

        btn.classList.remove('is-active');
        btn.nextElementSibling?.classList.add('is-active');
    }

    handleAdd(btn) {
        const limit = this.state.getMaxStickLimit();
        const current = this.state.getSelectedItems().length;
        if (current >= limit) return;

        const id = parseInt(btn.getAttribute('value'));
        this.state.addSelectedItem(id);
    }

    handleRemove(btn) {
        const id = parseInt(btn.getAttribute('value'));
        this.state.removeSelectedItem(id);
    }

    updateUI() {
        this.updateItemQuantities();
        this.updateFreeGiftCards();
        this.cartService.prepareCheckoutPayload(this.container);
        this.updateAddRemoveButtonStates();
    }

    updateAddRemoveButtonStates() {
        const selectedCount = this.state.getSelectedItems().length;
        const limit = this.state.getMaxStickLimit();
        const atLimit = selectedCount >= limit;

        this.container.querySelectorAll('[data-action-atc-add], [data-action-atc-add-quick]').forEach(el => {
            const isButton = el.tagName === 'BUTTON';
            if (isButton) el.disabled = atLimit;
            el.classList.toggle('is-disabled', atLimit);
        });

        this.container.querySelectorAll('[data-action-atc-rm]').forEach(btn => {
            btn.disabled = false;
            btn.classList.remove('is-disabled');
        });
    }

    updateItemQuantities() {
        const itemQuantMap = new Map();

        this.state.getSelectedItems().forEach(itemId => {
            itemQuantMap.set(itemId, (itemQuantMap.get(itemId) || 0) + 1);
        });

        this.itemQuantDivs.forEach(div => {
            const itemId = parseInt(div.id);
            const quantity = itemQuantMap.get(itemId) || 0;

            div.innerHTML = `${quantity}`;

            if (quantity === 0) {
                div.parentElement.classList.remove('is-active');
                div.parentElement.previousElementSibling?.classList.add('is-active');
                div.closest('[data-is-selected]')?.setAttribute('data-is-selected', 'false');
            } else {
                div.closest('[data-is-selected]')?.setAttribute('data-is-selected', 'true');
            }
        });
    }

    updateFreeGiftCards() {
        const giftCards = this.container.querySelectorAll('[data-gift-card]');
        if (!giftCards || giftCards.length === 0) {
            return;
        }

        const stickCount = this.state.getStickCount();
        const selectedCount = this.state.getSelectedItems().length;

        giftCards.forEach((card, i) => {
            // Skip cards already unlocked by stick count
            if (i < stickCount) {
                return;
            }

            // Unlock additional cards based on selection progress
            card.classList.toggle('is-active', i < selectedCount);
        });
    }
}

class CheckoutButtonController {
    constructor(container, config, state) {
        this.container = container;
        this.config = config;
        this.state = state;
        this.button = document.getElementById(this.config.CHECKOUT_BTN_ID);
        this.buttonText = this.button?.querySelector('[data-checkout-btn-text]');

        if (!this.button || !this.buttonText) {
            console.warn('Checkout button elements not found');
        }

        this.state.subscribe('selectionChanged', () => this.update());
    }

    update() {
        if (!this.button || !this.buttonText) return;

        const stickCount = this.state.getStickCount();
        const remaining = stickCount - this.state.getSelectedItems().length;

        if (remaining > 1) {
            let btnCopy = `Select ${remaining} more sticks`;
            if (this.config.language === 'es') {
                btnCopy = `Selecciona ${remaining} barras mas`;
            }
            this.buttonText.innerHTML = btnCopy;
            this.button.classList.remove('is-active');
            this.button.disabled = true;
        } else if (remaining === 1) {
            let btnCopy = 'Select your last stick';
            if (this.config.language === 'es') {
                btnCopy = 'Selecciona tu último barra';
            }
            this.buttonText.innerHTML = btnCopy;
            this.button.classList.remove('is-active');
            this.button.disabled = true;
        } else {
            let btnCopy = 'Continue';
            if (this.config.language === 'es') {
                btnCopy = 'Continuar';
            }
            this.buttonText.innerHTML = btnCopy;
            this.button.classList.add('is-active');
            this.button.disabled = false;
        }
    }

    showLoading() {
        if (!this.button) return;
        this.button.classList.add('sub-button--loading');
        this.button.disabled = true;
        setTimeout(() => {
            this.button.classList.remove('sub-button--loading');
            this.button.disabled = false;
        }, 10000);
    }
}

class ProductGroupController {
    constructor(container, config, state) {
        this.container = container;
        this.config = config;
        this.state = state;
        this.activeGroupCount = 0;
        this.productGroupButtons = [];
        this.wrapper = null;
        this.parentContainer = null;
        this.initialized = false;
        this.clickHandlers = new WeakMap();

        this.init();
    }

    init() {
        if (this.initialized) return;
        if (this.config.pageType === 'fem-care') return;

        this.wrapper = document.getElementById('suggested-group-wrapper');
        this.parentContainer = this.container.querySelector('[data-product-group-container]');

        if (!this.wrapper || !this.parentContainer) {
            console.warn('ProductGroupController: Required containers not found');
            return;
        }

        const allButtons = Array.from(
            this.container.querySelectorAll('.product-group-container [data-product-group-id]')
        );

        this.productGroupButtons = allButtons.filter(button => {
            return this.createProductGroupUI(button);
        });

        this.updateActiveGroupCount();
        this.attachEventListeners();
        this.initialized = true;
    }

    destroy() {
        this.productGroupButtons.forEach(button => {
            const handler = this.clickHandlers.get(button);
            if (handler) {
                button.removeEventListener('click', handler);
            }
        });
        this.clickHandlers = new WeakMap();
        this.productGroupButtons = [];
        this.activeGroupCount = 0;
        this.wrapper = null;
        this.parentContainer = null;
        this.initialized = false;
    }

    attachEventListeners() {
        this.productGroupButtons.forEach(button => {
            const handler = (e) => {
                e.preventDefault();

                if (button.dataset.processing === 'true') return;
                button.dataset.processing = 'true';

                Promise.resolve(this.handleButtonClick(button))
                    .catch(err => console.error('Button click error:', err))
                    .finally(() => {
                        button.dataset.processing = 'false';
                    });
            };

            this.clickHandlers.set(button, handler);
            button.addEventListener('click', handler);
        });
    }

    handleButtonClick(button) {
        this.productGroupButtons.forEach(btn => {
            btn.dataset.productGroupSelected = 'false';
        });

        button.dataset.productGroupSelected = 'true';
        return this.addProductGroupToCart(button);
    }

    createProductGroupUI(group) {
        if (!this.wrapper) return false;

        const groupId = group.dataset.productGroupId;
        const allVariantIds = group.dataset.productGroupVariantIds;

        if (!allVariantIds) {
            group.remove();
            return false;
        }

        const availableVariants = this.getAvailableVariants(allVariantIds);

        if (!availableVariants || availableVariants.length === 0) {
            group.remove();
            return false;
        }

        this.activeGroupCount++;

        const html = `
            <div class="suggested-group-container" 
                 data-product-group-id="${groupId}" 
                 data-product-group-variant-ids="${allVariantIds}" 
                 data-is-product-group-active="false" 
                 data-product-group-list="">
                <div class="suggested-group-title">${groupId} Bundle</div>
                <ul class="suggested-group-list-container">
                    ${availableVariants.map(v => `<li>${v.count}x ${v.title}</li>`).join('')}
                </ul>
            </div>
        `;

        this.wrapper.insertAdjacentHTML('beforeend', html);
        return true;
    }

    updateActiveGroupCount() {
        if (this.parentContainer) {
            this.parentContainer.dataset.activeProductGroups = this.activeGroupCount;
        }
    }

    getAvailableVariants(variantIdList) {
        const variantIds = variantIdList.split(',').map(id => id.trim()).filter(Boolean);
        const limit = this.state.getMaxStickLimit();
        const maxItems = limit === Infinity ? this.state.getStickCount() : limit;

        if (!maxItems || maxItems <= 0) return [];

        const variants = variantIds
            .map(id => {
                const variantDiv = document.getElementById(`ua-${id}`);
                if (!variantDiv) return null;

                const quickAddBtn = variantDiv.querySelector('[data-action-atc-add-quick]');
                const variantTitleContainer = variantDiv.querySelector('[data-variant-title]');

                if (!quickAddBtn || !variantTitleContainer) return null;

                return {
                    quickAddBtn,
                    title: variantTitleContainer.textContent.trim()
                };
            })
            .filter(Boolean);

        if (variants.length === 0) return [];

        const counts = new Map();

        for (let i = 0; i < maxItems; i++) {
            const variant = variants[i % variants.length];
            counts.set(variant.title, (counts.get(variant.title) || 0) + 1);
        }

        return variants
            .map(v => ({
                ...v,
                count: counts.get(v.title) || 0
            }))
            .filter(v => v.count > 0);
    }

    async addProductGroupToCart(selectedGroup) {
        try {
            if (!this.wrapper) {
                console.warn('ProductGroupController: Wrapper not available');
                return;
            }

            const prevGroup = this.wrapper.querySelector('[data-is-product-group-active="true"]');
            if (prevGroup) {
                prevGroup.dataset.isProductGroupActive = 'false';
            }

            const groupId = selectedGroup.dataset.productGroupId;

            let activeGroup = null;
            for (const container of this.wrapper.querySelectorAll('[data-product-group-id]')) {
                if (container.dataset.productGroupId === groupId) {
                    activeGroup = container;
                    break;
                }
            }

            if (!activeGroup) {
                console.warn('ProductGroupController: Active group not found');
                return;
            }

            activeGroup.dataset.isProductGroupActive = 'true';

            if (this.state.clearSelectedItems) {
                this.state.clearSelectedItems();
            }

            const variantIds = selectedGroup.dataset.productGroupVariantIds;
            if (!variantIds) {
                console.warn('ProductGroupController: No variant IDs found');
                return;
            }

            const selectedVariants = this.getAvailableVariants(variantIds);
            if (!selectedVariants || selectedVariants.length === 0) {
                console.warn('ProductGroupController: No available variants');
                return;
            }

            for (const variant of selectedVariants) {
                if (!variant.quickAddBtn || typeof variant.quickAddBtn.click !== 'function') {
                    console.warn('ProductGroupController: Invalid quick add button');
                    continue;
                }

                for (let i = 0; i < variant.count; i++) {
                    variant.quickAddBtn.click();
                }
            }
        } catch (error) {
            console.error('ProductGroupController: Failed to add product group to cart:', error);
        }
    }
}

// // ============================================
// // CAMPAIGN GATING
// // ============================================
// // Variants listed here are hidden unless the param matches campaignPrefix (via layout URL params API).
// // Param values and prefix matching are normalized by the URL params API: trim, unquote, case-insensitive.
// // Matching: allowed if param value starts with or includes any campaignPrefix string.
// // Format: { variantId: string, param: string, campaignPrefix: string[] } — param is the key within urlParams.
// // #campaignGatedVariants 
// // #updateProductStock
// const CAMPAIGN_GATED_VARIANTS = [
//     { variantId: '40797496770693', variantName: 'Bergamot & Oud', param: 'utm_content', campaignPrefix: ['Kasper_WMVolleyBallGirls'] },
//     { variantId: '40828340895877', variantName: 'Clean Sport', param: 'utm_content', campaignPrefix: ['Kasper_WMVolleyBallGirls'] },
// ];

// function applyCampaignGating(container) {
//     const api = window.urlParamsApi;
//     const getUrlParam = api?.getUrlParam || function (name) {
//         try {
//             const raw = JSON.parse(sessionStorage.getItem('urlParams') || '{}')[name];
//             if (raw == null || raw === '') return '';
//             const trimmed = String(raw).trim().replace(/^["']|["']$/g, '');
//             return trimmed.toLowerCase();
//         } catch (e) { return ''; }
//     };
//     const normalizeForMatch = api?.normalizeForMatch || function (value) {
//         if (value == null || value === '') return '';
//         const trimmed = String(value).trim().replace(/^["']|["']$/g, '');
//         return trimmed.toLowerCase();
//     };

//     const variantIdsToHide = [];

//     for (const rule of CAMPAIGN_GATED_VARIANTS) {
//         const value = getUrlParam(rule.param);
//         const allowed = rule.campaignPrefix.some(v => {
//             const prefix = normalizeForMatch(v);
//             return prefix !== '' && (value.startsWith(prefix) || value.includes(prefix));
//         });
//         if (!allowed) {
//             variantIdsToHide.push(rule.variantId);
//         }
//     }

//     for (const variantId of variantIdsToHide) {
//         const card = document.getElementById(`ua-${variantId}`);
//         if (card) card.style.display = 'none';
//     }

//     if (variantIdsToHide.length === 0) return;

//     const pills = container.querySelectorAll('[data-product-group-variant-ids]');
//     const hideSet = new Set(variantIdsToHide);

//     pills.forEach(pill => {
//         const current = pill.getAttribute('data-product-group-variant-ids') || '';
//         const ids = current.split(',').map(id => id.trim()).filter(Boolean);
//         const kept = ids.filter(id => !hideSet.has(id));
//         if (kept.length === 0) {
//             pill.remove();
//         } else {
//             pill.setAttribute('data-product-group-variant-ids', kept.join(','));
//         }
//     });
// }

// ============================================
// APPLICATION ORCHESTRATOR
// ============================================

class SaveLPApp {
    constructor() {
        this.container = document.getElementById('save-lp-page-container');

        if (!this.container) {
            console.error('Page container not found. Add #save-lp-page-container to page.');
            return;
        }

        this.config = new SaveLPConfig();
        this.state = new SaveLPState();
        this.trackingService = new TrackingService(this.state, this.config);
        this.cartService = new CartService(this.config, this.state, this.trackingService);

        this.init();
    }

    init() {
        if (!this.config.validate()) {
            console.error('Configuration validation failed');
            return;
        }

        // applyCampaignGating(this.container);

        const stickCount = this.state.getStickCount();

        // Initialize UI
        const initializer = new UIInitializer(this.container, this.config, this.state);
        initializer.initAll(stickCount);

        // Initialize Controllers
        new ProductSelectorController(this.container, this.state, this.cartService);
        initializer.bindGiftCountToState();
        const checkoutBtn = new CheckoutButtonController(this.container, this.config, this.state);
        this.productGroupController = new ProductGroupController(this.container, this.config, this.state);

        // Setup checkout button
        const checkoutBtnEl = document.getElementById(this.config.CHECKOUT_BTN_ID);
        if (checkoutBtnEl) {
            checkoutBtnEl.addEventListener('click', () => {
                checkoutBtn.showLoading();
                this.cartService.checkout();
            });
        }

        // Send tracking events
        this.trackingService.sendInitialEvents();

        this.state.subscribe('reachedMaxSticks', () => this.trackingService.sendConvertGoalMaxSticks());

        // Expose for global onclick handlers
        window.__saveLPApp = this;
    }
}

// ============================================
// INITIALIZE APP
// ============================================

document.addEventListener('DOMContentLoaded', () => {
    new SaveLPApp();
});

window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        console.log('Page restored from BF-Cache.');
        const atcButton = document.getElementById('save-lp-checkout-btn');
        atcButton?.classList.remove('sub-button--loading');
        atcButton.disabled = false;
    }
});

